import { Status } from '@/common/enums/Status';
import { AddressKind } from '@/common/enums/AddressKind';
import { ContactDestiny } from '@/common/enums/ContactDestiny';
import { PaymentKindId } from '@/common/enums/PaymentKind';
import { PaymentTermKind } from '@/common/enums/PaymentTermKind';
import { AvailabilityKind } from '@/common/enums/AvailabilityKind';
import { IstourreservationSet } from '@/accommodations/accommodations.model';
import { LocationKinds } from '@/common/enums/LocationKinds';
import { SlotKind } from '@/common/enums/SlotKind';
import { Product as MatProduct } from '@/accommodations/accommodations.model';
import { SelectedSeats } from '@oskar-frontend/comfort-package';
import { Insurance, Insurer } from '@/codebook/insurer/_types/insurer.type';
import { Voucher } from '@/vouchers/_model/voucher.model';

export interface IdName {
	id: number;
	name: string;
}

export interface Alert {
	id: string;
	active: boolean;
	reservation: number | null;
	reservationOffer: number | null;
	reservationSlot: number | null;
	lastModified: string | null;
	modifiedBy: ModifiedBy | null;
	alert: AlertNesting;
}

export interface ModifiedBy {
	id: number;
	firstName: string;
	lastName: string;
}

export interface AlertNesting {
	id: string;
	name: string;
	active: boolean;
	organizationDepartments: OwnerOrganizationDepartment[];
}

export interface Reservation {
	accessToken: string;
	invoiceNumber: string;
	invoice: boolean;
	alerts: Alert[];
	feeDueToCancellation: number;
	channel: string;
	confirmationTime: string;
	balanceWithoutCommission: number;
	balance: number;
	currency: Currency;
	expirationTime: string;
	id: number;
	isOnRequest: boolean;
	istourNumber: number;
	multimedias: Multimedia[];
	offer: Offer[];
	organization: FilteredOrganization;
	ownerOrganization: Organization;
	ownerUser: UserData;
	ownerNip: string;
	reservationTime: string;
	responsibleUser: User;
	seller: Organization;
	sellerCategory: SellerCategory;
	status: Status;
	time: {
		upper: string;
		lower: string;
	};
	totalPrice: number;
	paidAmount: number;
	services: SlotService[];
	commissionValue: number;
	cancellationCost: number;
	canceledAt: string;
	costToCancel: number;
	valueOfComponentsToCancel: number;
	nightsStay: number;
	days: number;
	voucher: Voucher | string;
	largeFamilyCard?: string;
}

export interface Multimedia {
	createdAt: string;
	createdBy: string;
	id: string;
	kind: string;
	name: string;
	url: string;
}

export interface SellerCategory {
	id: number;
	name: string;
	use: boolean;
	fee: number;
}

export interface UserContact {
	id: number;
	phone: string;
	email: string;
	destiny: Destiny;
}

export interface Destiny {
	id: ContactDestiny;
	destiny: string;
}

export interface UserAddress {
	zipCode: string;
	street: string;
	city: string;
}

export interface PaymentsTerm {
	id: number;
	term: string;
	currency: number;
	countValue: number;
	countKind: 'P' | 'V' | 'R';
	kind?: PaymentTermKind;
	value: number;
	previousValue?: number;
	daysBeforeDeparture?: number;
	status: string;
	table_id?: number;
	table_name?: string;
	source?: string;
	createdBy: User | null;
	modifiedBy: User | null;
	days?: number | null;
}

export interface PaymentDetails {
	id: number;
	kind: PaymentKind;
	userData: UserData;
	organization: number;
	bankAccountPosting: number;
	status: string;
	currency: Currency;
	payment: string;
	posting: string;
	amount: number;
	bankAccountIncoming: string;
	invoiceNumber: string;
	tableId: number;
	tableName: string;
	note: string;
}

export interface Nationality {
	id: number;
	name: string;
}

export interface OwnerUser {
	regularCustomer: boolean;
	id: number;
	user?: any;
	nationality: Nationality;
	birth: string;
	firstName: string;
	lastName: string;
	passportNumber: string;
	idNumber: string;
	shirtSize: string;
	googleId: string;
	sex: string;
	blackList: boolean;
	city: string;
	userConsent: UserConsent[];
	userContacts: UserContact[];
	userAddresses: UserAddress[];
	userDocuments?: UserDocumentResponse[];
}

export interface UserConsent {
	id: number;
	user: number;
	consent: Consent;
	kind: string;
	ip: string;
	paper: boolean;
}

interface Consent {
	id: number;
	consents: string;
	description: string;
	isRequired: boolean;
}

export interface UserData {
	id: number;
	user: number;
	nationality: IdName;
	birth: string;
	firstName: string;
	lastName: string;
	passportNumber: string;
	idNumber: string;
	shirtSize: string;
	googleId: string;
	sex: string;
	blackList: boolean;
	city: string;
	userConsent: UserConsent[];
	userContacts: UserContact[];
	userAddresses: UserAddress[];
	regularCustomer: boolean;
	userDocuments?: UserDocumentResponse[];
}

export interface PreCancellationPriceSummary {
	price: number;
	paidRatio: number;
}

export interface Slot {
	id: number;
	status: string;
	kind: SlotKind;
	userData: UserData;
	price: number;
	catalogPrice: number;
	priceWithoutPromotion: number;
	priceTransportReturn: number;
	priceTransportDeparture: number;
	priceBoard: number;
	promotionValue: number;
	days: number;
	transportDeparture: number;
	transportReturn: number;
	stopDeparture: StopObject;
	stopReturn: StopObject;
	slotServices: SlotService[];
	cancellationCost: number;
	totalPrice: number;
	feeDueToCancellation: number;
	costToCancel: number;
	seatnumber?: SeatNumber;
	priceAdjustment?: number;
	preCancellationPriceSummary?: PreCancellationPriceSummary | null;
	smsSent: boolean;
	flight?: SlotFlight;
}

export interface SlotFlight {
	departureTicket: string | null;
	returnTicket: string | null;
	isDataEntered: boolean;
	isDepartureCheckIn: boolean;
	isDepartureSent: boolean;
	isReturnCheckIn: boolean;
	isReturnSent: boolean;
	isCancellationInsuranceReported: boolean;
	cancellationInsuranceReportedAt: string | null;
}

export interface SeatNumber {
	id?: number;
	slot: number;
	singleDeckDeparture: Seat;
	singleDeckReturn: Seat;
	doubleDeckDeparture: Seat;
	doubleDeckReturn: Seat;
}

export interface Seat {
	column: number;
	id: number;
	kind: string;
	level: number;
	number: number;
	row: number;
}

export interface StopObject {
	id: number;
	transport: number;
	timeDeparture: string;
	fee: number;
	stop: Stop;
	flightNumber?: string | null;
	abroadAirportTime?: string | null;
	abroadAirportStop?: { id: number; name?: string; location?: { id: number; name: string } | null } | null;
}

export interface Stop {
	id: number;
	parentStop: Stop;
	location: Location;
	address: string;
	stopType: string;
}

export interface SlotService {
	id: number;
	slot?: number;
	price: number;
	countingMethod?: string;
	optionName?: string;
	value?: number;
	currency?: Currency;
	kinds: number[];
	isActive: boolean;
	description: string;
	code: string;
	overwrittenPrice: number | null;
	count: string;
	method: string;
	methodValue: number;
	service: OfferService;
}

export interface Transport {
	id: number;
	stop: TransportStop;
	timeDeparture: string;
	fee: string;
}

export interface Offer {
	id: number;
	roomType: RoomType;
	term: OfferTerm;
	product: OfferProduct;
	board: Board;
	promotion: OfferPromotion[];
	status: string;
	transportKind: string;
	slot: Slot[];
	roomAvailabilityState: RoomAvailabilityState;
	services: SlotService[];
	commissionValue: number;
	costToCancel: number;
	cancellationCost: number;
	totalPrice: number;
	totalPriceWithoutPromotion: number;
	cancellationPercentage: number;
	daysBeforeDeparture: number;
	oskarFault?: boolean;
	additionalRoomSizeCharge: number;
	wasTransportAvailable: boolean;
	offerChangeService: number;
	surchargeCurrency: number;
	surchargeFee: number;
	insurer: Insurer;
	insurance: Insurance;
}

export interface OfferChange extends Offer {
	totalPrice: number;
	newTotalPrice: number;
	slot: OfferChangeSlot[];
}

export interface OfferChangeSlot extends Slot {
	totalPrice: number;
	newTotalPrice: number;
	selectedStopReturn: ChangeOfferStop;
	selectedStopDeparture: ChangeOfferStop;
	servicesToAdd: FrontendOfferService[];
	selectedService: FrontendOfferService;
	slotServices: OfferChangeService[];
	newPrice: number;
	active: boolean;
	newBoardPrice: number;
	newRoomTypePrice: number;
	priceAdjustment?: number;
}

export interface OfferChangeService extends SlotService {
	active: boolean;
	newPrice: number;
}

export interface OfferProduct {
	organization: FilteredOrganization;
	id: number;
	name: string;
	code: string;
	conveniences: number[];
	productKind: ProductKind;
	location: Location[];
	insurer: Insurer;
}

export interface Location {
	id: number;
	name: string;
	code?: string;
	parentLocation?: Location | null;
	kind?: string;
	locationKind?: LocationKinds;
}

export interface OfferForState {
	id: number;
	transportKind: string;
	isOfferCombined: boolean;
	roomType: RoomType;
}

export interface SlotForState {
	id: number;
	roomGuarantee: boolean;
	viewGuarantee: boolean;
	userData: UserData;
}

export interface RoomAvailabilityState {
	id: number;
	room: number;
	dateRange: {
		lower: string;
		upper: string;
	};
	state: string;
	comment: string;
	istourreservationSet?: IstourreservationSet[];
	reservationofferSet?: OfferForState[];
	offerSlot?: SlotForState[];
	extendedStay?: number;
	ownTransport?: number;
	transportPlane?: number;
	roomGuarantee?: number;
	viewGuarantee?: number;
}

export interface RoomAvailabilityStateToPatch extends RoomAvailabilityState {
	destinationProduct: MatProduct;
}

export interface OfferPromotion {
	id: number;
	name: string;
	catalog: Catalog;
}

export interface Organization {
	address: OwnerOrganizationAddress[];
	id: number;
	shortName: string;
	name: string;
	sellCategory: number;
	regon: string;
	nip: string;
	krs: string;
	nawigatorID: number;
	isOskar: boolean;
	balance: number;
	travelOrganizationGovernmentApproval: string;
	departments: OwnerOrganizationDepartment[];
}

export interface OwnerOrganizationAddress {
	id: number;
	address: string;
	kind: OwnerOrganizationAddressKind;
	zipCode: string;
	location: OwnerOrganizationAddressLocation;
}

interface OwnerOrganizationAddressLocation {
	code: string;
	id: number;
	locationKind: string;
	name: string;
}

interface OwnerOrganizationAddressKind {
	id: AddressKind;
	kind: string;
}

export interface OwnerOrganizationDepartmentKind {
	id: number;
	name: string;
}

export interface User {
	id: number;
	firstName: string;
	lastName: string;
	email: string;
}

export interface OwnerOrganizationDepartment {
	id: number;
	kind: OwnerOrganizationDepartmentKind;
	userResponsibility: User;
	departmentContacts: OwnerOrganizationDepartmentContacts[];
}

export interface OwnerOrganizationDepartmentContacts {
	id: number;
	phone: string;
	email: string;
}

export interface Currency {
	id: number;
	name: string;
	code: string;
	exchangeRatePln: number;
}

export interface Room {
	id: number;
	name: string;
	code: string;
	configurations: RoomConfiguration[];
	kind: string;
}

export interface RoomConfiguration {
	id: number;
	kind: string;
	count: number;
	maxAge?: number;
	exchange_rate_pln: number;
}

export interface FilteredOrganization {
	id: number;
	name: string;
	shortName: string;
	isOskar: boolean;
}

export interface PaymentKind {
	id: PaymentKindId;
	description: string;
}

export interface BankAccount {
	id: number;
	currency: number;
	shortName: string;
	number: string;
	swift: string;
	organization: number;
	mainAccount: boolean;
	iban: string;
}

export interface Payment {
	id: number;
	kind: PaymentKind;
	userData: UserData;
	organization: number;
	bankAccountPosting: number;
	status: string;
	currency: number;
	payment: Date;
	posting: Date;
	amount: number;
	bankAccountIncoming: string;
	invoiceNumber: string;
	tableId?: number;
	tableName?: string;
	note: string;
	createdBy?: User | null;
	modifiedBy?: User | null;
}

export interface Board {
	id: number;
	name: string;
	code: string;
}

export interface OfferTerm {
	id: number;
	dateRange: {
		lower: string;
		upper: string;
		bounds?: string;
	};
	nightsStay: number;
	gratis: number;
}

export interface OfferTransport {
	id: number;
	name: string;
	side: string;
	kind: string;
	departureTerms: number[];
	returnTerms: number[];
}

export interface RoomType {
	id: number;
	name: string;
	code: string;
	kind: string;
	configuration: RoomTypeConfiguration[];
}

export interface RoomTypeConfiguration {
	id: number;
	kind: string;
	count: number;
	maxAge: number;
}

export interface TransportStop {
	id: number;
	address: string;
	stopType: string;
	location: StopLocation;
}

export interface StopLocation {
	id: number;
	name: string;
	code: string;
	locationKind: string;
}

export interface SummaryPayments {
	totalPrice: SummaryPaymentsValues;
	paymentBalance: SummaryPaymentsValues;
	commissionValue: SummaryPaymentsValues;
	totalPriceWithoutCommission: SummaryPaymentsValues;
	balance: SummaryPaymentsValues;
}

export interface SummaryPaymentsValues {
	value: number;
	name: string;
	disable: boolean;
}

export interface CancellationReason {
	id: number;
	name: string;
}

export interface OfferFull {
	board: {
		id: number;
		name: string;
		price: number;
	};
	catalogPrice: number;
	conveniences: { convenience: string; id: number; multimedia: Multimedia; name: string }[];
	dateRange: {
		lower: string;
		upper: string;
		bounds: string;
	};
	fullPrice: number;
	highlightLastSeats: boolean;
	locations: { id: number; kind: LocationKinds; name: string }[];
	multimedia: MultimediaWithOrder[];
	product: { id: number; name: string; kind: { id: number; name: string }; insurer: Insurer; insurance: Insurance };
	promotion: Promotion;
	roomType: { id: number; name: string; adults: number; children: number };
	services: PriceSettingService[];
	stop: {
		id: number;
		name: string;
		departureDateTime: number;
		returnDataTime: number;
		fee: number;
	};
	term: { id: number; nightStay: number; status: number; transportKind: string };
	transport: { kind: string; transportDeparture: number; transportReturn: number };
	transportKind: string;
}

export interface Board {
	board: number;
	name: string;
	boardPrice: number;
	currency: string;
	priceSetting: PriceSetting;
}

export interface PriceSetting {
	mainPrice: number;
	mainBoard: MainBoard;
	services: PriceSettingService[];
}

export interface MainBoard {
	id: number;
	name: string;
}

export interface PriceSettingService {
	optionName: string;
	value: number;
	countingMethod: string;
	currency: Currency;
}

export interface Stop {
	transport: Transport;
	name: string;
	address: string;
	stop: number;
	departureDatetime: string;
	returnDatetime: string;
	fee: number;
	kind: string;
	amount: number;
	amountFree: number;
}

export interface Transport {
	departureId: number;
	returnId: number;
	stopName: string;
	stopAddress: string;
	arrivalTime: string;
	departureTime: string;
}

export interface Room {
	term: Term;
	roomType: RoomType;
	fee: number;
	adults: number;
	children: number;
}

export interface Term {
	termId: number;
	product: Product;
	dateRange: {
		lower: string;
		upper: string;
	};
	sale: boolean;
	www: boolean;
	merlinx: boolean;
	promotion: boolean;
	nightsStay: number;
}

export interface Product {
	id: number;
	name: string;
	code: string;
	productKind: ProductKind;
	organization: number;
	locations: Location[];
	multimedias: MultimediaWithOrder[];
	descriptions: Description[];
	surchargeBoard: SurchargeBoard;
	surchargeProgram: SurchargeProgram;
	conveniences: Convenience[];
	tags: Tag[];
	defaultInsurance: Insurance;
	insurance: Insurance;
	insurer: Insurer;
}

export type ProductToServiceFilter = Pick<Product, 'id' | 'productKind'>;

export interface DateRange {
	upper: string;
	lower: string;
	bounds?: string;
}

export interface ProductKind {
	id: number;
	name: string;
	code: string;
}

export interface MultimediaWithOrder {
	multimedia: Multimedia;
	order: number;
}

export interface Multimedia {
	id: string;
	name: string;
	url: string;
}

export interface Description {
	description_header: DescriptionHeader;
	description: string;
}

export interface DescriptionHeader {
	id: number;
	name: string;
	multimedia: Multimedia;
}

export interface SurchargeBoard {
	id: number;
	fee: number;
	board: number;
	currency: Currency;
}

export interface Currency {
	id: number;
	name: string;
	code: string;
	exchangeRatePln: number;
}

export interface SurchargeProgram {
	id: number;
	description: string;
}

export interface Convenience {
	id: number;
	name: string;
	convenience: string;
	multimedia: Multimedia;
}

export interface Tag {
	id: number;
	name: string;
	tag: string;
}

export interface RoomType {
	id: number;
	name: string;
	code: string;
}

export interface Promotion {
	id: number;
	name: string;
	catalog: Catalog;
	description: string;
	method: string;
	methodValue: number;
}

export interface Catalog {
	id: number;
	name: string;
	multimedia: string;
}

export interface Payment {
	value: number;
	percentage: string;
	date: string;
}

export interface StopDefault {
	id: number;
	parent_stop: number;
	location: Location;
	address: string;
	stop_type: string;
	erp_grid: number[];
	longitude: string;
	latitude: string;
}

export interface ChangeOfferFilters {
	boards: number[];
	slots: number;
	roomTypes: number[];
	products: number[];
	conveniences: number[];
	locations: number[];
	stops: number[];
	productKinds: number[];
	promotion: number[];
	dateRange: {
		lower: string;
		upper: string;
	};
	nightsMin: number;
	nightsMax: number;
}

export interface ChangeOfferSelected {
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	product: any;
	promotion: Promotion;
	promotionValue: number;
	term: number;
	catalogPrice: number;
	dateRange: {
		lower: string;
		upper: string;
		bounds: string;
	};
	nightsStay: number;
	promotionCatalog: number;
	fee: number;
	fullPrice: number;
	priceDiff?: number;
	participants: { adults: number; children: number };
	board: {
		id: number;
		name: string;
		price: number;
		additionalRoomSizeCharge: number;
	};
	roomType: { id: number; name: string };
	surchargeForExtraRoomSpace: number;
	transportKind: string;
	stop: {
		id: number;
		name: string;
		transportDeparture: number;
		transportReturn: number;
	};
	wasTransportAvailable: boolean;
	surchargeFee: number;
	surchargeCurrency: Currency;
	insurer: number;
	insurance: Insurance;
}

export interface OfferService {
	id: number;
	name: string;
	code: string;
	serviceType: ServiceType;
	isActive: boolean;
	methodValue: number;
	dependsOn: string;
	countingMethod: string;
	description: string;
	insurer: Insurer | null;
}

export interface FrontendOfferService extends OfferService {
	serviceMultimedias?: ServiceMultimedia[];
	kinds?: number[];
	count?: string;
	method?: string;
	departures?: {
		lower: string | null;
		upper: string | null;
		bounds: string;
	};
	use?: string;
	importance?: number;
	allParticipant?: boolean;
}

export interface ServiceType {
	id: number;
	name: string;
}

export interface ServiceMultimedia {
	id: number;
	multimedia: Multimedia;
	order: number;
}

export interface ReservationOfferSend {
	term: number;
	roomType: number;
	product: number;
	board: number;
	promotion: number[];
	status: string;
	transportKind: string;
	slot: SlotOfferSend[];
	surchargeCurrency: number;
	surchargeFee: number;
	additionalRoomSizeCharge: number;
	insurance: boolean;
}

export interface SlotOfferSend {
	id: number;
	status: string;
	kind: string;
	userData: number;
	catalogPrice: number;
	feeDueToCancellation: number;
	priceBoard: number;
	transportDeparture: number;
	transportReturn: number;
	priceTransportDeparture: number;
	priceTransportReturn: number;
	stopDeparture: number;
	stopReturn: number;
	slotServices: {
		service: number;
	}[];
	priceAdjustment?: number;
}

export interface SearchedReservations {
	id: number;
	istourNumber: number;
	ownerUser: SearchedOwnerUser;
	ownerOrganization: SearchedOwnerOrganization;
	offer: { productName: string; status: Status }[];
}

export interface SearchedOwnerUser {
	id: number;
	firstName: string;
	lastName: string;
}

export interface SearchedOwnerOrganization {
	id: number;
	shortName: string;
}

export interface DocumentKind {
	name: string;
	displayName: string;
	params: { properties: any; required: string[] };
	uses: string[];
	version: string;
}

export interface ProfileDocumentKind {
	id: number;
	name: string;
}

export interface CreatedBy {
	id: number;
	firstName: string;
	lastName: string;
}

export interface Document {
	id: number;
	name: string;
	version: string;
	createdBy: CreatedBy;
	createdAt: string;
	params: any;
	reservations: number[];
}

export interface DocumentToSend {
	name: string;
	version: string;
	params: any;
	reservations: number[];
}

export interface DocumentList {
	id: number;
	name: string;
	createdAt: string;
	createdBy: {
		id: number;
		firstName: string;
		lastName: string;
		email: string;
	};
	url: string;
}

export interface UserDocumentCountryOfIssue {
	id?: number;
	name: string;
	code: string;
	locationKind: LocationKinds;
}

export interface UserDocumentPayload {
	id?: number;
	kind: number;
	expirationDate: string | Date | null;
	countryOfIssue: number | null;
	number: string;
}

export interface UserDocumentResponse {
	id?: number;
	kind: number;
	expirationDate: string;
	countryOfIssue: UserDocumentCountryOfIssue;
	number: string;
}

export interface UserDataFullPayload {
	birth: string;
	firstName: string;
	lastName: string;
	sex: string;
	userContacts: UserContactPayload[];
	nationality?: number;
	regularCustomer: boolean;
	userAddresses?: UserAddress[];
	userConsent?: UserConsentPayload[];
	idNumber: string;
	userDocuments: UserDocumentPayload[];
}

export interface PatchSeatNumberPayload extends SelectedSeats {
	slot: Slot['id'];
}

export interface UserConsentPayload extends Omit<UserConsent, 'consent'> {
	consent: number;
}

export interface UserContactPayload extends Omit<Omit<UserContact, 'destiny'>, 'id'> {
	destiny: ContactDestiny;
}

export interface PatchUserDataFullPayload extends UserDataFullPayload {
	id: number;
}

export interface Conversation {
	id: string;
	subject: string;
	date: string;
	unread: number;
	watching: boolean;
	labels: string[];
	tableId: number;
	tableName: string;
	replied: boolean;
}

export interface ConversationMessages {
	id: string;
	sender: string;
	subject: string;
	date: string;
	labels: string[];
	body: string;
	show: boolean;
	details: boolean;
	addressee: string;
	attachmentCount: number;
}

export interface Attachment {
	id: string;
	filename: string;
	mimeType: string;
	size: number;
	src?: string;
}

export interface AddedAttachment {
	filename: string;
	mimeType: string;
	content: string;
	size?: number;
}

export interface ChangeOfferStopFees {
	departure: number;
	return: number;
}

export interface ChangeOfferStop {
	stop: number;
	name: string;
	address: string;
	fees: ChangeOfferStopFees;
	transport: { departureId: number; returnId: number };
}

export type Seller = Omit<Organization, 'sellCategory'>;

export interface PatchReservationPayload
	extends Omit<Partial<Reservation>, 'id' | 'channel' | 'ownerUser' | 'ownerOrganization'> {
	id?: number;
	channel?: number;
	ownerUser?: number;
	ownerOrganization?: number;
}

export interface PatchOfferPayload extends Omit<Partial<Offer>, 'promotion' | 'insurance'> {
	promotion: number[];
	insurance: boolean;
}

export interface SeatNumberPayload {
	id?: number; // we dont need id to POST, but it's required for PATCH
	slot: number;
	singleDeckDeparture: number;
	singleDeckReturn: number;
	doubleDeckDeparture: number;
	doubleDeckReturn: number;
}

export type Channel = IdName;

export interface Result<T> {
	next: string | null;
	previous: string | null;
	results: T[];
	count: number;
}

export interface AvailabilityCheck {
	available: boolean;
	type: AvailabilityKind;
}

export interface AvailabilityTransport {
	returnId: number;
	departureId: number;
	stopId: number;
	personCount: number;
}

export interface Account {
	currency: Currency | number;
	id: string;
	iban: string;
	kind: number;
	isActive: boolean;
	mainAccount: boolean;
	number: string;
	swift: string;
	organization: number;
	fastAlior?: boolean;
}

export interface Currency {
	id: number;
	name: string;
	code: string;
	exchangeRatePln: number;
}

export interface OrganizationResponse {
	count: number;
	next: string;
	previous: string;
	results: Account[];
}

export interface OfferChangeDialog extends OfferChange {
	type: string;
	selectedService: FrontendOfferService;
}

export type Service = SlotService;

export interface OfferChangeStop {
	stop: number;
	name: string;
	address: string;
	fees: {
		departure: number;
		return: number;
	};
	transport: {
		departureId: number;
		returnId: number;
	};
}

export interface OfferSlotToPatch {
	reservation_offer?: number;
	kind?: SlotKind;
	userData?: number;
	seatNumbers?: SelectedSeats;
	status: string;
	slotServices?: { service: number }[];
	priceAdjustment?: number | null;
}

export const OWN_TRANSPORT_NAME = 'Dojazd własny';

export const NO_TRANSPORT_NAME = 'Brak transportu';

export interface DocumentSlotTransport {
	date: string;
	time: string;
	address: string;
}

export interface DocumentConvertedTransport {
	transportDeparture: DocumentSlotTransport;
	transportReturn: DocumentSlotTransport;
}

export interface ApiDate {
	date: string;
	time: string;
}

export interface SortedTermObj {
	term: string;
	value: number;
}

export interface ReservationSms {
	id: number;
	message: string;
	createdAt: string;
	recipient: SmsRecipient;
}

export interface SmsRecipient {
	firstName: string;
	lastName: string;
	userContacts: UserContact[];
}

export type SmsRecipientWithPhone = Omit<SmsRecipient, 'userContacts'> & { phone: string };

export type ReservationSmsWithPhone = Omit<ReservationSms, 'recipient'> & { recipient: SmsRecipientWithPhone };
